/**
 * DomainRedirect Component
 * 
 * Prüft, ob die aktuelle Domain mit der custom_domain der Organisation übereinstimmt.
 * Falls nicht, wird zur richtigen Domain weitergeleitet (mit dem gleichen Pfad und Query-Parametern).
 * 
 * Wird in TenantRoute verwendet, um sicherzustellen, dass Benutzer immer auf der
 * richtigen Produktions-Domain landen, auch wenn alte Links zur Preview-URL führen.
 */

import { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function DomainRedirect({ children }) {
    const { orgCode } = useParams();
    const location = useLocation();
    const [checking, setChecking] = useState(true);
    const [shouldRedirect, setShouldRedirect] = useState(false);
    const [targetUrl, setTargetUrl] = useState('');

    useEffect(() => {
        const checkDomain = async () => {
            if (!orgCode) {
                setChecking(false);
                return;
            }

            try {
                // Lade die Organisations-Info (öffentlicher Endpunkt)
                const response = await axios.get(`${API_URL}/api/organizations/${orgCode}/public`);
                const org = response.data;

                if (org?.custom_domain) {
                    const currentHost = window.location.hostname;
                    const customDomain = org.custom_domain.replace(/^https?:\/\//, '').replace(/\/$/, '');

                    // Prüfe, ob wir auf der falschen Domain sind
                    if (currentHost !== customDomain && 
                        !currentHost.includes('localhost') && 
                        !currentHost.includes('127.0.0.1')) {
                        
                        // Baue die neue URL zusammen
                        const newUrl = `https://${customDomain}${location.pathname}${location.search}`;
                        console.log(`[DomainRedirect] Redirecting from ${currentHost} to ${customDomain}`);
                        
                        setTargetUrl(newUrl);
                        setShouldRedirect(true);
                        
                        // Führe den Redirect aus
                        window.location.href = newUrl;
                        return;
                    }
                }
            } catch (error) {
                // Bei Fehler einfach normal weiter - nicht blockieren
                console.warn('[DomainRedirect] Could not check custom domain:', error.message);
            }

            setChecking(false);
        };

        checkDomain();
    }, [orgCode, location.pathname, location.search]);

    // Während des Redirects eine Ladeanimation anzeigen
    if (shouldRedirect) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-slate-600">Weiterleitung...</p>
                    <p className="text-xs text-slate-400 mt-2">
                        Sie werden zu {targetUrl.split('/')[2]} weitergeleitet
                    </p>
                </div>
            </div>
        );
    }

    // Während der Prüfung kurz laden (normalerweise sehr schnell)
    if (checking) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <div className="animate-pulse text-slate-400">Laden...</div>
            </div>
        );
    }

    // Keine Weiterleitung nötig - zeige die Kinder
    return children;
}
